import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {UseQueryResult} from 'react-query';
import {EnumSearchTypes, EnumTypVysledek, LayerVO, SearchResultVO, ServicesApi} from '../../api';
import {useMapioContext} from '../../mapioContext';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import {Coordinate, FeatureZoom, LayerId} from '@mc/react-mapio';
import {createCoordinateFeature, getSearchCoordinates} from '../../helpers/SearchUtils';
import {isUserDbLayer} from '../../helpers/LayerConfigUtils';
import {useAppConfig} from '@mc/react-app-base';
import {SearchListItem} from './searchPanelTypes';
import {buildLayerGroupNameMap, buildLegacySearchItems, buildSearchItems, SearchResultGroup} from '../../helpers/SearchRanking';
import {useCatalogLayers} from '../../hooks/useCatalogLayers';
import {useAtlasPristupnosti} from '../../hooks/useTopicsMap';

const SEARCH_MIN_CHARS = 3;
/** Panel hledání zobrazuje celý seznam výsledků (MCW-28421) – 0 = bez omezení počtu na skupinu. */
const PANEL_RESULT_LIMIT = 0;
/** Prodleva automatického hledání po dopsání (ms). Enter hledá okamžitě. */
const SEARCH_DEBOUNCE_MS = 350;

/** Skupiny lokačního hledání vrácené konektory převedeme na popisek jednoho výsledku. */
const getLocationResultTypeLabel = (groupLabel?: string): string | undefined => {
    switch (groupLabel?.toLocaleLowerCase('cs')) {
        case 'adresy':
            return 'Adresa';
        case 'ulice':
            return 'Ulice';
        case 'parcely':
            return 'Parcela';
        case 'location':
            return 'Lokalita';
        default:
            return groupLabel;
    }
};

type UseSearchPanelSearchParams = {
    /** Zavolá se po výběru feature (otevření detailu) – pro registraci "Zpět na seznam". */
    onFeatureSelected?: () => void;
    /** Zavolá se po kliknutí na LOKACI/adresu (ulice) – panel resetuje filtr a sbalí bottom sheet. */
    onLocationSelected?: () => void;
};

/**
 * Fulltextové hledání pro panel hledání – chování shodné se Search.tsx:
 * paralelní dotazy přes všechny EnumSearchTypes, deduplikace, seskupení POI vs. LOCATION
 * (LOCATIONS_PREVIEW), rozpoznání souřadnic a klik/hover s náhledem objektu i lokace.
 */
export const useSearchPanelSearch = ({onFeatureSelected, onLocationSelected}: UseSearchPanelSearchParams) => {
    const [searchText, setSearchText] = useState('');
    const [submittedText, setSubmittedText] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [items, setItems] = useState<SearchListItem[]>([]);
    const activeSearchIdRef = useRef<number>(0);
    // Poslední zvýrazněný objekt při hoveru – abychom ho na opuštění řádku odzvýraznili (necentrujeme).
    const hoveredPointerRef = useRef<{featureId: string; layerId: LayerId} | null>(null);
    // Časovač automatického hledání po debounce.
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const clearDebounce = useCallback(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
    }, []);

    const mapioContext = useMapioContext();
    const {mapRef, context, topicId, activeLayers, topic} = mapioContext.value;
    const {setSelectedDetailFeature, setActiveLayers} = mapioContext;
    const layersConfigQuery: UseQueryResult<{[key: string]: LayerVO}> = useLayersConfig();
    // @ts-ignore
    const {serverPublic} = useAppConfig();
    // Řazení podle relevance a slučování skupin platí jen pro organizace s příznakem „Atlas přístupnosti“
    // (MCW-29581); bez něj panel zobrazuje výsledky v původním pořadí a členění.
    const atlasPristupnosti: boolean = useAtlasPristupnosti();
    // Katalog vrstev – název nadřazené (hlavní) skupiny, pod kterou se sloučí výsledky dílčích vrstev.
    const catalogLayersQuery = useCatalogLayers(topicId);
    const layerGroupNames: Map<number, string> = useMemo(
        () => (atlasPristupnosti ? buildLayerGroupNameMap(catalogLayersQuery.data) : undefined),
        [catalogLayersQuery.data, atlasPristupnosti],
    );

    const allSearchableLayerList = useMemo((): string => {
        if (!layersConfigQuery.data) return null;
        return Object.values(layersConfigQuery.data)
            .filter((l: LayerVO) => l.searchable)
            .map((l: LayerVO) => l.id)
            .join(',');
    }, [layersConfigQuery.data]);

    /**
     * Náhled objektu (select=klik) nebo lokace. Na klik (select) objekt vybere a otevře detail, lokaci přesune
     * mapu. Na hover (select=false) objekt POUZE zvýrazní – NECENTRUJE (centrování při hoveru se chovalo divně).
     */
    const previewResult = useCallback(
        (value: SearchListItem, select?: boolean) => {
            if (value.typVysledek === EnumTypVysledek.LAYER && !value.attrs?.featureId) return;
            if (value.attrs?.featureId) {
                const featurePointer = {featureId: value.attrs.featureId, layerId: value.attrs.layer as LayerId};
                if (select) {
                    // Zhasnout případný hover, ať po výběru nezůstane viset zvýraznění (dotyk – bez mouseleave).
                    if (hoveredPointerRef.current) {
                        mapRef?.current?.highlightFeature(null, true);
                        hoveredPointerRef.current = null;
                    }
                    mapRef?.current?.selectFeatures([featurePointer], {zoom: FeatureZoom.CENTER});
                    setSelectedDetailFeature([featurePointer]);
                } else {
                    // Hover: jen zvýraznit objekt (bez posunu mapy).
                    hoveredPointerRef.current = featurePointer;
                    mapRef?.current?.highlightFeature(featurePointer, true);
                }
            } else if (select) {
                // Lokace / adresa (LOCATIONS_PREVIEW) – nemá featureId, jen pozici. Přesun mapy jen na klik.
                mapRef?.current?.activateObjectPreview(true, value);
                mapRef?.current?.setLocation(value.attrs?.position as Coordinate);
            }
        },
        [mapRef, setSelectedDetailFeature],
    );

    const runSearch = useCallback(
        (text: string) => {
            const coordinates = getSearchCoordinates(text);
            if (coordinates) {
                mapRef?.current?.unloadFeatures(['coordinate-result']);
                mapRef?.current?.loadFeatures([createCoordinateFeature(coordinates)], {zoom: FeatureZoom.CENTER_AND_ZOOM});
                setItems([]);
                setIsSearching(false);
                return;
            }

            const searchId = ++activeSearchIdRef.current;
            setIsSearching(true);
            setItems([]);

            // Panel výsledky vykresluje PRŮBĚŽNĚ, jak dílčí dotazy dobíhají (na rozdíl od našeptávače v Search.tsx,
            // kde se podle MCW-29537 čeká na všechny) – uživatel tu vidí ulice hned, aniž by čekal na objekty.
            // Seskupení, řazení podle relevance a limit na skupinu se přepočítají při každé došlé odpovědi.
            const collectedGroups = new Map<string, SearchResultGroup<SearchListItem>>();
            const seenFeatureKeys = new Set<string>();
            const types = Object.values(EnumSearchTypes);
            let completedCount = 0;

            /** Přepočítá a zobrazí to, co je zatím posbírané. */
            const showCollectedResults = (): void => {
                const {items: builtItems, foundCount} = atlasPristupnosti
                    ? buildSearchItems(Array.from(collectedGroups.values()), {
                          locationsFirst: topic?.showLocationsFirst,
                          layerGroupNames,
                          // Panel je procházetelný seznam výsledků (MCW-28421), takže se tu na 10 neořezává.
                          limit: PANEL_RESULT_LIMIT,
                      })
                    : buildLegacySearchItems(Array.from(collectedGroups.values()), {locationsFirst: topic?.showLocationsFirst});
                setItems(foundCount > 0 ? [{statusRow: true, count: foundCount}, ...builtItems] : builtItems);
            };

            types.forEach(type => {
                // Hledání ignoruje aktivní tag-filtr – prohledáváme VŠECHNY prohledávatelné vrstvy
                // („objekty se ve vyhledávači vykreslí standardně, bez filtru").
                const featuresList = allSearchableLayerList;
                ServicesApi.searchLayer(topicId, {
                    searchText: text,
                    type,
                    context,
                    features: featuresList,
                    // Limit posílá jen atlasový režim; bez příznaku server výsledky neomezuje ani nepřerovnává.
                    resultLimit: atlasPristupnosti ? PANEL_RESULT_LIMIT : undefined,
                })
                    .then(response => {
                        if (searchId !== activeSearchIdRef.current) return;
                        const data = response.data;
                        Object.keys(data.layerInfo ?? {}).forEach((key: string) => {
                            const layerInfo = data.layerInfo?.[key];
                            const layerIdNum = Number(key) as LayerId;
                            const newFeatures: SearchListItem[] = (data.results?.[key] ?? [])
                                .filter((item: SearchResultVO) => {
                                    if (layerInfo?.typVysledek === EnumTypVysledek.LAYER && !item.attrs?.featureId && activeLayers.includes(layerIdNum)) {
                                        return false;
                                    }
                                    const featureKey = `${key}:${item.attrs?.featureId ?? item.id}`;
                                    if (seenFeatureKeys.has(featureKey)) return false;
                                    seenFeatureKeys.add(featureKey);
                                    return true;
                                })
                                .map(
                                    (item: SearchResultVO): SearchListItem => ({
                                        ...item,
                                        typVysledek: layerInfo?.typVysledek,
                                        resultTypeLabel:
                                            layerInfo?.typVysledek === EnumTypVysledek.LOCATION ? getLocationResultTypeLabel(layerInfo?.nazev) : undefined,
                                    }),
                                );

                            if (newFeatures.length === 0) return;
                            const group = collectedGroups.get(key);
                            if (group) {
                                group.items.push(...newFeatures);
                            } else {
                                collectedGroups.set(key, {key, name: layerInfo?.nazev, typVysledek: layerInfo?.typVysledek, items: newFeatures});
                            }
                        });
                        showCollectedResults();
                    })
                    .catch(() => {
                        /* ignorujeme dílčí chyby */
                    })
                    .finally(() => {
                        if (searchId !== activeSearchIdRef.current) return;
                        completedCount++;
                        if (completedCount === types.length) setIsSearching(false);
                    });
            });
        },
        [allSearchableLayerList, topicId, context, activeLayers, topic?.showLocationsFirst, layerGroupNames, atlasPristupnosti, mapRef],
    );

    const onTextChange = useCallback(
        (text: string) => {
            setSearchText(text);
            clearDebounce();
            // Krátký/prázdný text → vrátit panel do režimu výřezu (zrušit odeslané hledání).
            if (!text || text.length < SEARCH_MIN_CHARS) {
                activeSearchIdRef.current++;
                setSubmittedText('');
                setItems([]);
                setIsSearching(false);
                return;
            }
            // Automatické hledání po krátkém debounce (bez tlačítka „Vyhledat").
            debounceRef.current = setTimeout(() => {
                setSubmittedText(text);
                runSearch(text);
            }, SEARCH_DEBOUNCE_MS);
        },
        [runSearch, clearDebounce],
    );

    // Enter = okamžité vyhledání (přeskočí debounce).
    const onSubmit = useCallback(() => {
        clearDebounce();
        if (!searchText || searchText.length < SEARCH_MIN_CHARS) return;
        setSubmittedText(searchText);
        runSearch(searchText);
    }, [searchText, runSearch, clearDebounce]);

    const onClear = useCallback(() => {
        clearDebounce();
        activeSearchIdRef.current++;
        mapRef?.current?.unloadFeatures(['coordinate-result']);
        mapRef?.current?.activateObjectPreview(false);
        setSearchText('');
        setSubmittedText('');
        setItems([]);
        setIsSearching(false);
    }, [mapRef, clearDebounce]);

    // Úklid časovače při odmountování.
    useEffect(() => clearDebounce, [clearDebounce]);

    /** Klik na položku výsledku – aktivace vrstvy (LAYER) nebo výběr objektu / lokace. */
    const onResultClick = useCallback(
        (item: SearchListItem) => {
            if (item.title || item.statusRow) return;
            const layerId = item.attrs?.layer as LayerId;

            // LAYER výsledek (bez featureId) na vypnuté vrstvě → zapnout vrstvu a zoomovat.
            if (item.typVysledek === EnumTypVysledek.LAYER && !item.attrs?.featureId && !activeLayers.includes(layerId)) {
                const newLayers = [...activeLayers, layerId];
                setActiveLayers(newLayers);
                mapRef?.current?.setLayers(newLayers);
                mapRef?.current?.zoomOnLayer(layerId, {padding: [100, 100, 300, 100]});
                return;
            }

            // user_db LAYER výsledek na aktivní vrstvě (ne serverPublic) – v Search.tsx zakomentováno, nic neděláme.
            if (
                !serverPublic &&
                !item.attrs?.featureId &&
                item.typVysledek === EnumTypVysledek.LAYER &&
                isUserDbLayer(layersConfigQuery.data, layerId) &&
                activeLayers.includes(layerId)
            ) {
                return;
            }

            previewResult(item, true);
            if (item.attrs?.featureId) {
                onFeatureSelected?.();
            } else {
                // Lokace/adresa (ulice) bez featureId – panel resetuje filtr a sbalí bottom sheet.
                onLocationSelected?.();
            }
        },
        [activeLayers, setActiveLayers, mapRef, serverPublic, layersConfigQuery.data, previewResult, onFeatureSelected, onLocationSelected],
    );

    const onResultHover = useCallback(
        (item: SearchListItem) => {
            if (item.title || item.statusRow) return;
            previewResult(item);
        },
        [previewResult],
    );

    const onResultLeave = useCallback(() => {
        // Zrušit zvýraznění posledního objektu (hover jen zvýrazňuje, nemá preview lokace).
        // Zhasnutí hoveru = null + hover=true; předání pointeru s hover=false naopak vytvoří TRVALÝ
        // highlight, který v mapě zůstane viset.
        if (hoveredPointerRef.current) {
            mapRef?.current?.highlightFeature(null, true);
            hoveredPointerRef.current = null;
        }
        mapRef?.current?.activateObjectPreview(false);
    }, [mapRef]);

    // Panel je v režimu hledání teprve po odeslání dotazu (kliknutí na "Vyhledat" / Enter), ne při psaní.
    const isSearchActive = submittedText.length >= SEARCH_MIN_CHARS;

    return {
        searchText,
        isSearching,
        items,
        minChars: SEARCH_MIN_CHARS,
        isSearchActive,
        onTextChange,
        onSubmit,
        onClear,
        onResultClick,
        onResultHover,
        onResultLeave,
    };
};
